import {
  AmenitiesStateParkIcon,
  GeneralClockIcon,
  GeneralMapPinOutlineIcon,
  GeneralNavigationIcon,
} from '@outdoorsyco/bonfire';
import React, { createElement, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ISuggestion } from '@/components/ui/HomeSearchWidget/LocationFilterContext';
import { useGeoDataMyIp } from '@/hooks/useGeoDataMyIp';
import { stripDiacritics } from '@/utility/stripDiacritics';

export interface ILocationSearchAutocompleteListOption {
  label: string;
  groupName: string;
  value?: any;
  url?: string;
  sublabel?: string;
  icon?: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
}

export interface ILocationSearchAutocompleteListGroup {
  options: ILocationSearchAutocompleteListOption[];
  key: number;
  index: number;
  group: string;
}

interface ILocationSearchAutocompleteOptionsParams {
  shouldBuildGroupedOptions: boolean;
  suggestions: ISuggestion[];
  recentSearches?: ISuggestion[];
  popularDestinations?: ISuggestion[];
  includeCurrentLocation?: boolean;
  includeRecentSearches?: boolean;
  includePopularDestinations?: boolean;
}

interface ILocationSearchAutocompleteOptionsReturn {
  options: ILocationSearchAutocompleteListOption[];
  groupedOptions: ILocationSearchAutocompleteListGroup[];
  optionGroupLabels: {
    suggestions: string;
    recentSearches: string;
    popularDestinations: string;
  };
}

export const useLocationSearchAutocompleteOptions = ({
  suggestions,
  shouldBuildGroupedOptions,
  recentSearches = [],
  popularDestinations = [],
  includeCurrentLocation = true,
  includeRecentSearches = true,
  includePopularDestinations = true,
}: ILocationSearchAutocompleteOptionsParams): ILocationSearchAutocompleteOptionsReturn => {
  const intl = useIntl();
  const { data: geoData } = useGeoDataMyIp();

  const optionGroupLabels = useMemo(
    () => ({
      suggestions: intl.formatMessage({
        defaultMessage: 'Suggested Locations',
        id: 'hlQMdt',
      }),
      recentSearches: intl.formatMessage({
        defaultMessage: 'Recent Searches',
        id: 'UE9zNR',
      }),
      popularDestinations: intl.formatMessage({
        defaultMessage: 'Popular Destinations',
        id: 'QSEbp/',
      }),
    }),
    [intl],
  );

  const suggestionOptions = useMemo(() => {
    const result: ILocationSearchAutocompleteListOption[] = [];

    const currentLocationValue = geoData ? `${geoData.city}, ${geoData.state}` : '';

    const suggestionsContainingCurrentLocation = suggestions.filter(suggestion => {
      const input = currentLocationValue.trim().toLowerCase();
      const candidate = stripDiacritics(suggestion.label.trim().toLowerCase());

      return input.indexOf(candidate) > -1;
    });

    if (
      (suggestions.length === 0 || suggestionsContainingCurrentLocation.length > 0) &&
      includeCurrentLocation &&
      currentLocationValue
    ) {
      result.push({
        label: intl.formatMessage({
          defaultMessage: 'Current location',
          id: 'xf5BmJ',
        }),
        sublabel: currentLocationValue,
        groupName: optionGroupLabels.suggestions,
        value: {
          address: currentLocationValue,
        },
        icon: props =>
          createElement(GeneralNavigationIcon, { className: 'w-6 h-6 text-gray-900', ...props }),
      });
    }

    result.push(
      ...suggestions.map(
        option =>
          ({
            ...option,
            groupName: optionGroupLabels.suggestions,
            icon: props =>
              createElement(GeneralMapPinOutlineIcon, {
                className: 'w-6 h-6 text-gray-900',
                ...props,
              }),
          }) as ILocationSearchAutocompleteListOption,
      ),
    );

    return result;
  }, [geoData, intl, suggestions, includeCurrentLocation, optionGroupLabels.suggestions]);

  const recentSearchOptions = useMemo(() => {
    return recentSearches.map(
      option =>
        ({
          ...option,
          groupName: optionGroupLabels.recentSearches,
          icon: props =>
            createElement(GeneralClockIcon, { className: 'w-6 h-6 text-gray-900', ...props }),
        }) as ILocationSearchAutocompleteListOption,
    );
  }, [recentSearches, optionGroupLabels.recentSearches]);

  const popularDestinationOptions = useMemo(() => {
    return popularDestinations.map(
      option =>
        ({
          ...option,
          groupName: optionGroupLabels.popularDestinations,
          value: {
            address: option.value,
          },
          icon: props =>
            createElement(AmenitiesStateParkIcon, {
              className: 'w-6 h-6 text-gray-900',
              ...props,
            }),
        }) as ILocationSearchAutocompleteListOption,
    );
  }, [popularDestinations, optionGroupLabels.popularDestinations]);

  const { options, groupedOptions } = useMemo(() => {
    const optionsResult: ILocationSearchAutocompleteListOption[] = [];
    const groupedOptionsResult: ILocationSearchAutocompleteListGroup[] = [];

    if (shouldBuildGroupedOptions) {
      let groupIndex = 0;

      if (suggestionOptions.length > 0) {
        groupedOptionsResult.push({
          options: suggestionOptions,
          key: groupIndex,
          index: groupIndex,
          group: optionGroupLabels.suggestions,
        });
        groupIndex++;
      }

      if (includeRecentSearches && recentSearchOptions.length > 0) {
        groupedOptionsResult.push({
          options: recentSearchOptions,
          key: groupIndex,
          index: groupIndex,
          group: optionGroupLabels.recentSearches,
        });
        groupIndex++;
      }

      if (includePopularDestinations && popularDestinationOptions.length > 0) {
        groupedOptionsResult.push({
          options: popularDestinationOptions,
          key: groupIndex,
          index: groupIndex,
          group: optionGroupLabels.popularDestinations,
        });
        groupIndex++;
      }
    } else {
      optionsResult.push(...suggestionOptions);

      if (includeRecentSearches) {
        optionsResult.push(...recentSearchOptions);
      }
      if (includePopularDestinations) {
        optionsResult.push(...popularDestinationOptions);
      }
    }
    return { options: optionsResult, groupedOptions: groupedOptionsResult };
  }, [
    shouldBuildGroupedOptions,
    suggestionOptions,
    recentSearchOptions,
    popularDestinationOptions,
    includeRecentSearches,
    includePopularDestinations,
    optionGroupLabels,
  ]);

  return {
    options,
    groupedOptions,
    optionGroupLabels,
  };
};
